/**
 * La boleta v2: la calificación de 0 a 10 donde cada criterio pesa lo que
 * cuesta no hacerlo.
 *
 * Vive junto a la v1, no en su lugar. La v1 (0-100) sigue siendo la
 * calificación de referencia mientras Gerencia no decida otra cosa; ésta se
 * muestra al lado para que las dos se puedan comparar sobre la misma llamada.
 *
 * Es un componente compartido porque lo usan el detalle de admin (`CallDetail`)
 * y el del agente (`AgentCallDetail`), y lo que ve el agente tiene que ser
 * palabra por palabra lo que ve su supervisor: si difieren, la sesión de
 * retroalimentación arranca discutiendo la herramienta en vez de la llamada.
 *
 * Los pesos NO se escriben aquí. Llegan en la respuesta desde
 * `app/rubrica_v2.py`, que es donde se calculó la calificación. Una copia en
 * JavaScript sería la manera segura de que un día el peso mostrado y el peso
 * aplicado dejen de coincidir.
 */

import { useState } from 'react';
import { corregirCriterio } from '../api';

const VERDE = '#34d399';
const ROJO = '#f87171';
const GRIS = '#6b7280';
const INDIGO = '#818cf8';

/** Color de la calificación sobre 10. Los cortes son los de la v1, a escala. */
export function colorV2(nota) {
  if (nota === null || nota === undefined) return GRIS;
  if (nota >= 8) return VERDE;
  if (nota >= 6) return '#fbbf24';
  return ROJO;
}

/**
 * El número, para ponerlo al lado del score v1 en el encabezado.
 *
 * Devuelve null cuando la llamada no tiene boleta v2 — anterior al 1-ago-2026,
 * saliente, o sin diálogo aprovechable. En esos casos la pantalla muestra sólo
 * la v1, que es exactamente lo pedido: el histórico no cambia de aspecto.
 */
export function CalificacionV2({ boleta }) {
  if (!boleta || boleta.calificacion === null || boleta.calificacion === undefined) return null;
  const color = colorV2(boleta.calificacion);
  return (
    <div
      className="analysis-score-circle"
      style={{ borderColor: color }}
      title={`Score V2 (${boleta.rubrica_version}) — de 10, ponderado por lo que cada conducta aporta a la conversión. No es comparable con el Score de 0 a 100.`}
    >
      <span className="analysis-score-value" style={{ color, fontSize: 22 }}>
        {boleta.calificacion.toFixed(1)}
      </span>
      <span className="analysis-score-label">Score V2</span>
    </div>
  );
}

function Veredicto({ valor }) {
  if (valor === 'si') return <span style={{ color: VERDE, fontWeight: 600 }}>sí</span>;
  if (valor === 'no') return <span style={{ color: ROJO, fontWeight: 600 }}>no</span>;
  return <span style={{ color: GRIS }} title="No se presentó la situación que este criterio evalúa, así que sale del cálculo">no aplicó</span>;
}

/**
 * La boleta completa. Va DEBAJO de los validadores de la v1.
 *
 * `onDescartar` es lo que habilita la corrección de la penalización, y es
 * OPCIONAL a propósito: quien la pasa es el detalle de admin (`CallDetail`); el
 * del agente no la pasa y por eso ahí no hay botones. La capacidad la concede
 * el padre en vez de que este componente pregunte por el rol — y el backend la
 * exige de todos modos con `require_admin`.
 *
 * El agente SÍ ve el estado "descartado": es su calificación la que cambió, y
 * enterarse por un número distinto sin explicación es peor que no cambiarlo.
 */
export default function BoletaV2({ boleta, onDescartar = null, callId = null, puedeCorregir = false, onCorregido = null }) {
  // Qué criterio se está corrigiendo. Uno a la vez: dos filas abiertas invitan a
  // teclear un motivo en una y guardarlo en la otra.
  const [corrigiendo, setCorrigiendo] = useState(null);
  const [motivoCorreccion, setMotivoCorreccion] = useState('');
  const [errorCorreccion, setErrorCorreccion] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [err, setErr] = useState(null);
  const [abierto, setAbierto] = useState(false);
  const [nota, setNota] = useState('');

  if (!boleta) return null;

  const { criterios = [], perdidos = [], calificacion } = boleta;
  // `cumple` es el veredicto AUTOMÁTICO, así que la fila sigue encontrándose
  // aunque alguien la haya descartado. Si buscáramos por el efectivo, una
  // penalización descartada desaparecería de la pantalla y con ella el deshacer.
  const penalizado = criterios.find((c) => c.penalizacion && c.cumple === 'si');
  const descartado = Boolean(penalizado?.descartado);
  const noAplican = criterios.filter((c) => !c.penalizacion && c.cumple !== 'si' && c.cumple !== 'no');

  const aplicarCorreccion = (clave, veredicto) => {
    setErrorCorreccion(null);
    Promise.resolve(corregirCriterio(callId, clave, veredicto, motivoCorreccion || null))
      .then(() => {
        setCorrigiendo(null);
        setMotivoCorreccion('');
        if (onCorregido) onCorregido();
      })
      .catch((e) => setErrorCorreccion(
        e?.response?.data?.detail
          ? (typeof e.response.data.detail === 'string'
              ? e.response.data.detail
              : 'Falta el motivo para bajar el criterio.')
          : 'No se pudo corregir.'
      ));
  };

  const enviar = (esError) => {
    setGuardando(true);
    setErr(null);
    Promise.resolve(onDescartar(esError, esError === false ? nota : null))
      .then(() => { setAbierto(false); setNota(''); })
      .catch((e) => setErr(e?.response?.data?.detail || 'No se pudo guardar.'))
      .finally(() => setGuardando(false));
  };

  const th = {
    textAlign: 'left', padding: '6px 8px', fontSize: 11, color: GRIS,
    textTransform: 'uppercase', letterSpacing: 0.4, borderBottom: '1px solid #26262e',
    fontWeight: 600,
  };
  const td = { padding: '7px 8px', fontSize: 13, borderBottom: '1px solid #1c1c22' };

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-title" style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <span>Score V2 — cada criterio pesa lo que cuesta no hacerlo</span>
        <span style={{ fontSize: 11, color: GRIS, textTransform: 'none', letterSpacing: 0 }}>
          {boleta.rubrica_version} · no sustituye al Score de arriba
        </span>
      </div>

      {/* El encabezado: el número y, si perdió puntos, POR QUÉ. Esta línea es la
          que convierte la boleta en material de coaching — sin ella el agente
          ve una calificación y no sabe qué habría cambiado. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap', marginBottom: 14 }}>
        <div style={{ fontSize: 34, fontWeight: 700, color: colorV2(calificacion), lineHeight: 1 }}>
          {calificacion === null || calificacion === undefined ? '—' : calificacion.toFixed(1)}
          <span style={{ fontSize: 15, color: GRIS, fontWeight: 400 }}> / {boleta.total ?? 10}</span>
        </div>
        <div style={{ fontSize: 12, color: '#9ca3af', flex: 1, minWidth: 240 }}>
          {/* Un guión sin explicación se lee como "el sistema falló". Aquí se
              dice qué pasó: aplicó tan poco de la rúbrica que ponerle nota
              sería inventar precisión. */}
          {boleta.insuficiente && (
            <>
              <span style={{ color: '#e0e0e6' }}>Sin calificación: </span>
              de los {boleta.total ?? 10} puntos de la rúbrica sólo aplicaron{' '}
              <strong>{boleta.aplicables?.toFixed(1)}</strong>, menos del mínimo de{' '}
              {boleta.minimo_aplicable}. Calificar sobre esa fracción daría un número
              que parece una nota y no lo es.
            </>
          )}
          {!boleta.insuficiente && perdidos.length === 0 && !penalizado
            && 'Cumplió todos los criterios que aplicaron.'}
          {!boleta.insuficiente && perdidos.length > 0 && (
            <>
              <span style={{ color: '#e0e0e6' }}>Lo que costó: </span>
              {perdidos.map((p, i) => (
                <span key={p.etiqueta}>
                  {i > 0 && ' · '}
                  {p.etiqueta} <strong style={{ color: ROJO }}>−{p.peso.toFixed(1)}</strong>
                </span>
              ))}
            </>
          )}
          {/* La penalización es lo único de la boleta que se afirma en negativo,
              y era lo único sin sustento: la pantalla decía "−1.0" y quien la
              recibía no tenía cómo validarla. La cita va AQUÍ además de en su
              renglón porque es donde se lee la nota, y es donde empieza la
              discusión. */}
          {/* En su propia caja. Antes era un renglón más entre los cuatro del
              encabezado, y la única acción de la boleta que cambia una
              calificación quedaba indistinguible de la explicación de la nota.
              El borde no es adorno: es lo que hace que el ojo caiga aquí al
              llegar a la tarjeta. */}
          {penalizado && (
            <div style={{
              marginTop: 8, padding: '7px 10px', borderRadius: 6,
              color: descartado ? GRIS : ROJO,
              background: descartado ? 'rgba(107, 114, 128, 0.08)' : 'rgba(248, 113, 113, 0.07)',
              borderLeft: `3px solid ${descartado ? GRIS : ROJO}`,
            }}>
              {descartado ? (
                /* Con AUTOR y fecha. Una corrección sin nombre es
                   indistinguible de un cambio del sistema, y ésta cambió una
                   calificación. */
                <span>
                  Error descartado{penalizado.descartado_por ? ` por ${penalizado.descartado_por}` : ''}
                  {penalizado.descartado_at ? ` el ${penalizado.descartado_at.slice(0, 10)}` : ''}
                  {' '}— no resta puntos.
                </span>
              ) : (
                <span style={{ fontWeight: 600 }}>
                  Penalización por error: −{Math.abs(penalizado.peso).toFixed(1)}
                </span>
              )}
              {penalizado.extracto ? (
                /* La cita se conserva al descartar: es lo que se leyó para
                   decidir, y quitarla dejaría la corrección sin nada que la
                   explique. Tachada, porque ya no cuenta. */
                <div style={{
                  marginTop: 3, color: '#9ca3af', fontStyle: 'italic',
                  textDecoration: descartado ? 'line-through' : 'none',
                }}>
                  «{penalizado.extracto}»
                </div>
              ) : (
                /* Callar la ausencia haría creer que no hay dónde mirar. Decirla
                   deja claro que la penalización es vieja, no infundada. */
                <div style={{ marginTop: 3, color: GRIS, fontStyle: 'normal' }}>
                  Esta boleta no guardó la frase que lo sustenta. El sustento se
                  captura desde el 27-ago-2026; para revisarlo, la transcripción
                  está abajo.
                </div>
              )}
              {descartado && penalizado.descartado_nota && (
                <div style={{ marginTop: 3, color: GRIS }}>
                  Motivo: {penalizado.descartado_nota}
                </div>
              )}

              {/* Los controles sólo existen si el padre los concedió.
                  BOTÓN, no enlace, y con la palabra que usa quien lo busca.
                  Nació como un enlace subrayado de 11 px en la misma tinta que
                  el resto del texto, al 64% de una página de 3,563 px: estaba
                  en la pantalla y aun así "no estaba disponible" —reportado el
                  28-ago-2026 por quien pidió la función—. Un control que hay que
                  saber que existe para encontrarlo no existe. */}
              {onDescartar && !descartado && !abierto && (
                <button type="button" style={btnFalsoPositivo} disabled={guardando}
                        onClick={() => setAbierto(true)}>
                  Marcar como falso positivo
                </button>
              )}
              {onDescartar && !descartado && abierto && (
                <div style={{ marginTop: 5, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    value={nota} onChange={(e) => setNota(e.target.value)}
                    placeholder="motivo (opcional)"
                    style={{
                      background: '#131318', border: '1px solid #26262e', borderRadius: 4,
                      color: '#e0e0e6', fontSize: 11, padding: '3px 6px', minWidth: 200,
                    }}
                  />
                  <button type="button" style={btnFalsoPositivo} disabled={guardando}
                          onClick={() => enviar(false)}>
                    {guardando ? 'Guardando...' : 'Descartar y recalcular'}
                  </button>
                  <button type="button" style={btnMini}
                          onClick={() => { setAbierto(false); setNota(''); setErr(null); }}>
                    Cancelar
                  </button>
                </div>
              )}
              {onDescartar && descartado && (
                <button type="button" style={btnMini} disabled={guardando}
                        onClick={() => enviar(null)}>
                  Volver al automático
                </button>
              )}
              {err && (
                <div style={{ marginTop: 3, color: ROJO, fontSize: 11 }}>{err}</div>
              )}
            </div>
          )}
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>Criterio</th>
              <th style={{ ...th, textAlign: 'right', width: 60 }}>Peso</th>
              <th style={{ ...th, textAlign: 'right', width: 110 }} title="Puntos porcentuales de conversión que se pierden cuando la conducta falta, entre llamadas comparables">
                No hacerlo cuesta
              </th>
              <th style={{ ...th, width: 80 }}>Veredicto</th>
            </tr>
          </thead>
          <tbody>
            {criterios.map((c) => (
              <tr key={c.clave} style={{ opacity: c.cumple === 'na' || c.cumple == null ? 0.5 : 1 }}>
                <td style={td}>
                  {c.etiqueta}
                  {c.extracto && (
                    <div style={{ fontSize: 11, color: GRIS, fontStyle: 'italic', marginTop: 2 }}>
                      "{c.extracto}"
                    </div>
                  )}
                </td>
                <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: c.penalizacion ? ROJO : INDIGO, fontWeight: 600 }}>
                  {c.peso > 0 ? c.peso.toFixed(1) : `−${Math.abs(c.peso).toFixed(1)}`}
                </td>
                <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: GRIS }}>
                  {c.cuesta_pp !== null && c.cuesta_pp !== undefined
                    ? `${c.cuesta_pp.toFixed(1)} pp`
                    : c.penalizacion ? 'política' : 'prerrequisito'}
                </td>
                <td style={td}>
                  {/* La fila tiene que decir lo mismo que el encabezado: un "sí"
                      pelón aquí, con "descartado" arriba, es la misma boleta
                      afirmando dos cosas. */}
                  {c.descartado
                    ? <span style={{ color: GRIS }} title="Un revisor determinó que no fue un error, así que no resta puntos">descartado</span>
                    : <Veredicto valor={c.cumple_efectivo ?? c.cumple} />}
                  {/* Que hubo una decisión humana, y de quién. Mostrar sólo el
                      resultado esconde que alguien la tomó. */}
                  {c.correccion && (
                    <div style={{ fontSize: 10, color: '#fbbf24', marginTop: 2 }}
                         title={`El análisis dijo "${c.cumple}". ${c.correccion.motivo || ''}`}>
                      corregido por {c.correccion.por}
                    </div>
                  )}
                  {puedeCorregir && !c.penalizacion && (
                    corrigiendo === c.clave ? (
                      <div style={{ marginTop: 4 }}>
                        {/* El motivo sólo hace falta para BAJAR: quitarle puntos
                            a alguien por juicio propio tiene que quedar
                            argumentado; devolvérselos, no. */}
                        <input
                          value={motivoCorreccion}
                          onChange={(e) => setMotivoCorreccion(e.target.value)}
                          placeholder="motivo (obligatorio si bajas a no)"
                          style={{
                            width: 190, background: '#131318', border: '1px solid #26262e',
                            borderRadius: 4, color: '#e0e0e6', fontSize: 10.5, padding: '2px 5px',
                          }}
                        />
                        <div style={{ display: 'flex', gap: 6, marginTop: 3, flexWrap: 'wrap' }}>
                          <button type="button" style={btnMini}
                                  onClick={() => aplicarCorreccion(c.clave, 'si')}>sí</button>
                          <button type="button" style={btnMini}
                                  onClick={() => aplicarCorreccion(c.clave, 'no')}>no</button>
                          {c.correccion && (
                            <button type="button" style={btnMini}
                                    onClick={() => aplicarCorreccion(c.clave, null)}>quitar</button>
                          )}
                          <button type="button" style={btnMini}
                                  onClick={() => { setCorrigiendo(null); setMotivoCorreccion(''); setErrorCorreccion(null); }}>
                            cancelar
                          </button>
                        </div>
                        {errorCorreccion && (
                          <div style={{ fontSize: 10, color: ROJO, marginTop: 3 }}>{errorCorreccion}</div>
                        )}
                      </div>
                    ) : (
                      <button type="button" style={btnCorregir}
                              onClick={() => { setCorrigiendo(c.clave); setMotivoCorreccion(''); setErrorCorreccion(null); }}>
                        corregir
                      </button>
                    )
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Sin esta nota, un "no aplicó" se lee como un criterio incumplido — que
          es justo el defecto de la v1 que la v2 vino a corregir. */}
      {noAplican.length > 0 && !boleta.insuficiente && (
        <div style={{ marginTop: 10, fontSize: 11, color: GRIS }}>
          {noAplican.length} {noAplican.length === 1 ? 'criterio no aplicó' : 'criterios no aplicaron'} en
          esta llamada y salen del cálculo: la calificación se reparte sobre los{' '}
          <strong style={{ color: '#9ca3af' }}>{boleta.aplicables?.toFixed(1)}</strong> puntos
          que sí aplicaban. No cuentan como incumplidos.
        </div>
      )}

      <div style={{ marginTop: 8, fontSize: 10.5, color: '#4b5563' }}>
        Pesos medidos sobre {boleta.medicion}. Calibración provisional: se
        reajustan cuando haya datos suficientes con los criterios ya definidos.
      </div>
    </div>
  );
}

const btnMini = {
  background: 'none', border: 'none', padding: 0, marginTop: 4, cursor: 'pointer',
  color: '#818cf8', fontSize: 11, textDecoration: 'underline',
};

/**
 * La acción de descartar la penalización. Es la ÚNICA de esta tarjeta que
 * cambia una calificación, así que se ve como un control y no como texto: borde,
 * fondo y 12 px, en el rojo de la penalización que va a quitar — no en el índigo
 * genérico de todos los enlaces de la aplicación.
 */
/**
 * El control de corregir un criterio, en cada renglón.
 *
 * Nació como `btnMini` —11 px, subrayado, en el índigo de todos los enlaces— y
 * eso ya había fallado una vez con el botón del falso positivo: reportado el
 * 28-ago y arreglado, y reproducido catorce veces aquí el 31-ago. Un control
 * que hay que saber que existe para encontrarlo no existe, y estos catorce son
 * los únicos —junto al descarte— que mueven una CALIFICACIÓN.
 *
 * Chip con borde y no botón grande: van uno por renglón en una tabla de quince,
 * así que tienen que verse sin dominar la lectura del veredicto, que es el dato.
 */
const btnCorregir = {
  marginTop: 4, cursor: 'pointer',
  background: 'rgba(129,140,248,0.10)',
  border: '1px solid rgba(129,140,248,0.40)',
  borderRadius: 4, padding: '2px 8px',
  color: '#a5b4fc', fontSize: 11, fontWeight: 600,
};

const btnFalsoPositivo = {
  marginTop: 7, cursor: 'pointer',
  background: 'rgba(248, 113, 113, 0.10)',
  border: '1px solid rgba(248, 113, 113, 0.45)',
  borderRadius: 5, padding: '4px 10px',
  color: '#f87171', fontSize: 12, fontWeight: 600,
};
